import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { Util } from '@grandvisionhq/common';
import { useIntl } from '@grandvisionhq/state';
import { isNumber, createScriptTag, injectTags } from '@grandvisionhq/utils';
import { createPowerReviewsConfig, mergeConfigs } from '../utils/create-powerreviews-config.js';
import { REVIEWS_EVENT_ID, REVIEW_SNIPPET_ID } from './constants.js';
import { reviewsReducer, initialState } from './reducer.js';

// These are the keys PowerReviews requires in the `components` object when initializing a widget
const mapStateToPowerReviewsType = {
    reviewWrite: 'Write',
    reviewSnippet: 'ReviewSnippet',
    reviewDisplay: 'ReviewDisplay'
};
const ReviewsContext = /*#__PURE__*/ React.createContext({});
const useReviewsContext = ()=>React.useContext(ReviewsContext)
;
const PR_SCRIPT_ID = 'power-reviews-script';
const PR_SCRIPT_SRC = 'https://ui.powerreviews.com/stable/4.1/ui.js';
const ReviewsProvider = ({ enabled , config , children ,  })=>{
    const [state, dispatch] = React.useReducer(reviewsReducer, initialState);
    const [reviewCount, setReviewCount] = React.useState(null);
    const [hasError, setHasError] = React.useState(false);
    const { locale  } = useIntl();
    const widgets = React.useMemo(()=>{
        return Object.keys(state).reduce((result, key)=>{
            if (state[key] !== null) {
                const widgetConfig = createPowerReviewsConfig(swcHelpers.objectSpread({
                    config,
                    locale
                }, state[key], {
                    type: mapStateToPowerReviewsType[key]
                }));
                widgetConfig.on_read_reviews_click = ()=>{
                    const customEvent = Util.createCustomEvent(REVIEWS_EVENT_ID);
                    window.dispatchEvent(customEvent);
                    DataLayer.Events.pushDrawerEventToDataLayer({
                        siteFeature: 'reviews',
                        interactionType: 'customer_reviews_click'
                    });
                };
                // PowerReviews exports a on_write_reviews_link event, that gets triggered when the reviews link is clicked.
                // However, when it gets clicked, e.preventDefault() is called, preventing the actual click on the anchor tag
                // to trigger navigation to a new page. Therefore, on_render is used to add an event listener to the
                // write review link instead.
                widgetConfig.on_render = function(_, data) {
                    if (isNumber(data === null || data === void 0 ? void 0 : data.review_count)) {
                        setReviewCount(data.review_count);
                    }
                    const writeReviewSnippet = document.getElementById(REVIEW_SNIPPET_ID);
                    const writeReviewLink = writeReviewSnippet === null || writeReviewSnippet === void 0 ? void 0 : writeReviewSnippet.getElementsByClassName('pr-snippet-write-review-link')[0];
                    writeReviewLink === null || writeReviewLink === void 0 ? void 0 : writeReviewLink.addEventListener('click', pushWriteReviewToDataLayer);
                };
                return [
                    ...result,
                    widgetConfig
                ];
            }
            return result;
        }, []);
    }, [
        state
    ]);
    React.useEffect(()=>{
        const script = createScriptTag({
            id: PR_SCRIPT_ID,
            src: PR_SCRIPT_SRC,
            defer: true,
            onError: ()=>{
                setHasError(true);
            }
        });
        injectTags([
            script
        ]);
    }, []);
    React.useEffect(()=>{
        if (enabled && widgets.length) {
            const mergedConfig = widgets.length > 1 ? mergeConfigs(widgets) : widgets[0];
            if (!mergedConfig) {
                return undefined;
            }
            removeDataLayerEventHandler();
            // Clear existing widgets and loaders before (re)initializing
            Object.values(mergedConfig.components).forEach((widgetId)=>{
                clearWidgetHtml(widgetId);
            });
            // Set powerreviews configuration. powerreviews has a complex async script loading and does not provide a proper load hook.
            // To prevent a race condition we prepare the config beforehand.
            // This is the official way powerreviews should be declared.
            window.pwr = window.pwr || function(...args) {
                (pwr.q = pwr.q || []).push(args);
            };
            window.pwr('render', mergedConfig);
            // When new widgets are updated, first remove the old from powerreviews as they might have the same id and therefore will not be rendered.
            return ()=>{
                removeDataLayerEventHandler();
                Object.values(mergedConfig.components).forEach((widgetId)=>{
                    window.pwr('unmount', widgetId);
                });
            };
        }
    }, [
        widgets,
        enabled
    ]);
    const value = React.useMemo(()=>({
            enabled,
            config,
            dispatch,
            reviewCount,
            hasError
        })
    , [
        enabled,
        config,
        reviewCount,
        hasError
    ]);
    return /*#__PURE__*/ React.createElement(ReviewsContext.Provider, {
        value: value
    }, children);
};
// Because PowerReviews doesn't clear their widget's HTML themselves
// when we re-call their `display` method
function clearWidgetHtml(id) {
    const element = document.getElementById(id);
    if (element) {
        element.innerHTML = '';
    }
}
const removeDataLayerEventHandler = ()=>{
    const writeReviewLink = document.getElementsByClassName('pr-snippet-write-review-link')[0];
    writeReviewLink === null || writeReviewLink === void 0 ? void 0 : writeReviewLink.removeEventListener('click', pushWriteReviewToDataLayer);
};
const pushWriteReviewToDataLayer = ()=>{
    DataLayer.Events.pushDrawerEventToDataLayer({
        siteFeature: 'reviews',
        interactionType: 'write_first_review_click'
    });
};

export { PR_SCRIPT_ID, PR_SCRIPT_SRC, ReviewsProvider, ReviewsContext as default, mapStateToPowerReviewsType, useReviewsContext };
//# sourceMappingURL=ReviewsContext.js.map
